import { Lock } from "lucide-react";
import { COMPANY } from "@/lib/site";

// Marchio testuale/SVG segnaposto — sostituire con il logo ufficiale Supernova
export function LogoMark({ className = "w-8 h-8 text-[#ff6a00]" }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3" />
      <circle cx="16" cy="16" r="6" fill="#ff6a00" />
      <path d="M16 2V8M16 24V30M2 16H8M24 16H30" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function CosmicBackground() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden="true">
      {/* Base: blu intenso luminoso che sfuma verso il viola */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, #10307a 0%, #16296b 30%, #241c63 58%, #10173a 100%)" }}
      ></div>
      {/* Luce blu intensa in alto */}
      <div className="absolute top-[-22%] left-1/2 -translate-x-1/2 w-[95vw] h-[65vh] rounded-full blur-[150px] bg-[#3b82f6]/35"></div>
      {/* Sfumatura viola ampia a sinistra */}
      <div className="absolute top-[22%] left-[-12%] w-[58vw] h-[58vw] rounded-full blur-[150px] bg-gradient-to-tr from-[#7c3aed]/40 via-[#8b5cf6]/20 to-transparent"></div>
      {/* Tocco beige / oro chiaro luminoso in alto a destra */}
      <div className="absolute top-[0%] right-[-8%] w-[46vw] h-[46vw] rounded-full blur-[120px] bg-gradient-to-bl from-[#eed9a8]/35 via-[#dcbd85]/18 to-transparent sn-pulse"></div>
      {/* Arancione caldo in basso a destra */}
      <div className="absolute bottom-[-15%] right-[6%] w-[52vw] h-[52vw] rounded-full blur-[140px] bg-gradient-to-tl from-[#ff9440]/25 via-[#7c3aed]/18 to-transparent"></div>
      {/* Linee di energia */}
      <svg className="absolute inset-0 w-full h-full opacity-25">
        <defs>
          <radialGradient id="sn-glow" cx="50%" cy="26%" r="55%">
            <stop offset="0%" stopColor="#eed9a8" stopOpacity="0.30" />
            <stop offset="45%" stopColor="#8b5cf6" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#10173a" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="sn-beam" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7db4ff" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#b79dfa" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#f2bc6a" stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#sn-glow)" />
        <line x1="0" y1="20%" x2="100%" y2="80%" stroke="url(#sn-beam)" strokeWidth="1" strokeDasharray="8 12" />
        <line x1="10%" y1="100%" x2="90%" y2="0" stroke="url(#sn-beam)" strokeWidth="1" strokeDasharray="6 14" />
      </svg>
    </div>
  );
}

export function SiteHeader() {
  return (
    <header className="relative z-20 border-b border-white/10 backdrop-blur-md bg-[#10307a]/60">
      <nav className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between" aria-label="Principale">
        <a href="#top" className="flex items-center" data-testid="header-logo-link">
          <img
            src="/logo-supernova.webp"
            alt="Supernova Energy Network — logo ufficiale"
            className="h-12 sm:h-14 w-auto"
            data-testid="header-logo"
          />
        </a>
        <div className="flex items-center gap-4">
          <a
            href="#partner"
            data-testid="header-area-partner-link"
            className="hidden sm:inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-slate-300 hover:text-white px-3 py-1.5 rounded border border-white/15 hover:border-white/40 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#f43f5e]"></span>
            Area Partner
          </a>
          <a
            href="#contattaci"
            data-testid="header-cta-consulenza"
            className="bg-gradient-to-r from-[#ff6a00] to-[#f43f5e] hover:brightness-110 text-white font-medium text-xs uppercase tracking-wider px-5 py-2.5 rounded-full shadow-lg shadow-[#ff6a00]/20 transition"
          >
            Richiedi Consulenza
          </a>
        </div>
      </nav>
    </header>
  );
}

export function SiteFooter({ onPrivacy }) {
  return (
    <footer className="border-t border-white/10 bg-[#0a1230] text-slate-400 py-12 px-6 relative z-10" data-testid="site-footer">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div>
          <img
            src="/logo-supernova.webp"
            alt="Supernova Energy Network"
            className="h-7 w-auto mb-2"
            data-testid="footer-logo"
          />
          <p className="text-xs text-slate-500 font-mono" data-testid="footer-legal">{COMPANY.legal}</p>
        </div>
        <div className="flex flex-wrap items-center gap-6 text-xs font-mono">
          <span className="text-slate-500" data-testid="footer-email">{COMPANY.email}</span>
          <button type="button" onClick={onPrivacy} data-testid="footer-privacy-link" className="text-slate-400 hover:text-white transition-colors">
            Privacy Policy
          </button>
          <button type="button" onClick={onPrivacy} data-testid="footer-cookie-link" className="text-slate-400 hover:text-white transition-colors">
            Cookie Policy
          </button>
        </div>
      </div>
    </footer>
  );
}

export function PrivacyDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" role="dialog" aria-modal="true" data-testid="privacy-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative max-w-lg w-full rounded-2xl border border-white/15 bg-[#0a0d1e] p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-5 h-5 text-[#ff6a00]" />
          <h3 className="font-display text-xl uppercase text-white tracking-wide">Informativa Privacy</h3>
        </div>
        <div className="text-sm text-slate-300 space-y-3 leading-relaxed">
          <p>
            I dati inseriti nei moduli di questo sito (nome, contatti, messaggio ed eventuali allegati)
            sono utilizzati esclusivamente per rispondere alla tua richiesta o valutare la tua candidatura.
          </p>
          <p>
            Le informazioni vengono trasmesse in modo sicuro all'indirizzo email aziendale e non vengono
            pubblicate, cedute a terzi né utilizzate per finalità di marketing senza consenso.
          </p>
          <p className="text-xs text-slate-500 font-mono">
            Documento informativo sintetico — l'informativa completa sarà pubblicata con i dati del titolare del trattamento.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="privacy-dialog-close"
          className="mt-6 w-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-mono text-xs uppercase tracking-widest py-3 rounded-lg transition-colors"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
}
